import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

/**
 * Phase 3.56 — Datenschutzerklärung für twowheels.info.
 *
 * Feb-2026 (User-Wunsch 25 May, Update v5): inhaltlich auf KI-Verarbeitung
 * (Vision, Voice, Document Scan), Cookies, Drittanbieter (Stripe, Resend,
 * Twilio, Google), Wallet & Vermittler-Tracking erweitert. Vor Live-
 * Deployment durch anwaltlich geprüfte Endfassung ersetzen.
 */
export default function DatenschutzPage() {
    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="legal-datenschutz-page">
            <Link to={-1} onClick={(e) => { e.preventDefault(); window.history.back(); }} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#111827] mb-4">
                <ArrowLeft className="w-4 h-4" /> Zurück
            </Link>
            <div className="flex items-center gap-3 mb-6">
                <ShieldCheck className="w-7 h-7 text-[#FFCC00]" />
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Datenschutzerklärung</h1>
            </div>
            <p className="text-sm text-gray-500 mb-8">Stand: Juni 2026 · twowheels.info</p>

            <article className="prose prose-sm max-w-none space-y-6 text-[#111827]">
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">1. Verantwortliche Stelle</h2>
                    <p>
                        TwoWheels · Inhaber: Jörg Scholz · Pasinger Straße 12 · 12309 Berlin · Deutschland<br />
                        E-Mail: <a href="mailto:datenschutz@twowheels.info" className="underline">datenschutz@twowheels.info</a>
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">2. Welche Daten wir verarbeiten</h2>
                    <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Stammdaten:</strong> Name, E-Mail, Telefonnummer, Anschrift, Firmen-/Steuerdaten bei B2B.</li>
                        <li><strong>Inhaltsdaten:</strong> Inserate (Fahrzeugdaten, Bilder, Videos, Beschreibungen), Nachrichten zwischen Nutzern, Bewertungen, Trade-In-Angebote, Gebote.</li>
                        <li><strong>Identifikations-/Authentifizierungsdaten:</strong> Login-Sessions, Passwort-Hashes (bcrypt), Google-OAuth-Tokens.</li>
                        <li><strong>Nutzungsdaten:</strong> Login-Zeiten, gekürzte IP-Adresse, Geräteinformationen, Cookies, Klick-/Scroll-Verhalten innerhalb der Plattform.</li>
                        <li><strong>Zahlungsdaten:</strong> nur bei kostenpflichtigen Buchungen — die Abwicklung erfolgt über Stripe. Kartendaten werden NICHT auf unseren Servern gespeichert.</li>
                        <li><strong>Standortdaten:</strong> PLZ + Ort (selbstdeklariert für Inserate), keine GPS-Tracking.</li>
                        <li><strong>Audio &amp; Bild-Uploads:</strong> Sprachaufnahmen (KI-Sprachaufnahme), hochgeladene Fotos und Dokumente (ZB1, COC, Service-Heft).</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">3. Zwecke der Verarbeitung</h2>
                    <p>
                        Bereitstellung der Marktplatzfunktionen (Anzeigen, Suche, Filter, Chat, Auktion, Trade-In, Sharing),
                        Vertragsanbahnung zwischen Nutzern, Identifizierung von Händlern, Wallet-Buchhaltung (€-Punkte),
                        Vermittler-Provisionen, Betrugsprävention, Sicherheit der Plattform, Erfüllung gesetzlicher
                        Aufbewahrungspflichten (z. B. § 257 HGB / § 147 AO), und auf Wunsch Marketing-Kommunikation.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">4. Rechtsgrundlagen</h2>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Art. 6 Abs. 1 lit. b DSGVO — Vertragserfüllung</li>
                        <li>Art. 6 Abs. 1 lit. c DSGVO — gesetzliche Verpflichtung</li>
                        <li>Art. 6 Abs. 1 lit. f DSGVO — berechtigtes Interesse (Betrugsabwehr, Plattformsicherheit)</li>
                        <li>Art. 6 Abs. 1 lit. a DSGVO — Einwilligung (Newsletter, optionale Cookies)</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">5. KI-Verarbeitung deiner Inhalte</h2>
                    <p>
                        Zur Verbesserung deines Inserats nutzen wir KI-gestützte Hilfsfunktionen:
                    </p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Vision-Analyse:</strong> hochgeladene Fotos werden an Google Gemini Vision gesendet, um automatisch Labels (Fahrzeugfront, Cockpit, Schaden …), Schadensbefunde und einen Qualitäts-Score zu erstellen.</li>
                        <li><strong>Sprach-Zu-Inserat:</strong> Sprachaufnahmen werden lokal komprimiert und an OpenAI Whisper / Google Gemini übermittelt zur Transkription und Feld-Extraktion.</li>
                        <li><strong>Dokumenten-Scan:</strong> ZB1, ZB2, COC und Service-Heft-Fotos werden an Google Gemini Vision gesendet zur strukturierten Feldextraktion (HSN, TSN, VIN, Erstzulassung, Vorbesitzer-Daten …).</li>
                        <li><strong>Textgenerierung:</strong> Beschreibungs-, Service-Note-, Plausibilitäts- und Social-Caption-Vorschläge werden via Gemini / Claude / GPT erzeugt.</li>
                    </ul>
                    <p>
                        Eingaben werden NICHT zum dauerhaften Training fremder LLM-Modelle verwendet (Geschäftskunden-API
                        mit Daten-Nicht-Training). Die übermittelten Daten verlassen den EU-Wirtschaftsraum nur, sofern
                        der jeweilige Drittanbieter über entsprechende Standardvertragsklauseln (SCC) verfügt.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">6. Empfänger / Auftragsverarbeiter</h2>
                    <p>Wir setzen folgende Dienstleister ein (jeweils mit AVV nach Art. 28 DSGVO):</p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Google Cloud (USA / EU):</strong> OAuth-Login, Gemini-LLM für KI-Hilfsfunktionen</li>
                        <li><strong>OpenAI (USA):</strong> Whisper (Sprache-zu-Text) und ergänzende LLMs</li>
                        <li><strong>Anthropic (USA):</strong> Claude-LLM für Plausibilitäts-Checks und Smart-Replies</li>
                        <li><strong>Stripe (Irland / USA):</strong> Zahlungsabwicklung für Premium-Funktionen</li>
                        <li><strong>Resend (USA):</strong> Transaktionale E-Mails</li>
                        <li><strong>Twilio (USA):</strong> SMS- &amp; WhatsApp-Benachrichtigungen (optional)</li>
                        <li><strong>Skribble (Schweiz / EU):</strong> qualifizierte/fortgeschrittene elektronische Signatur (Leasingrückgabe, Verträge)</li>
                        <li><strong>Eigene Server (EU):</strong> MongoDB-Datenbank und Object-Storage für Bilder/Dokumente</li>
                    </ul>
                    <p className="mt-3">
                        <strong>Rollenverteilung:</strong> Soweit Händler die Plattform nutzen, um Daten ihrer
                        eigenen Kund:innen (z. B. Leasingnehmer:innen, Werkstatt- und Kaufkund:innen) zu
                        verarbeiten, ist der jeweilige Händler <em>Verantwortlicher</em> und twowheels.info
                        <em> Auftragsverarbeiter</em> nach Art. 28 DSGVO. Hierfür stellen wir Händlern einen
                        Auftragsverarbeitungsvertrag (AVV) inkl. Liste der Unterauftragsverarbeiter bereit.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">7. Google-Anmeldung (OAuth)</h2>
                    <p>
                        Wenn du dich per Google anmeldest, übermittelt Google uns nur deinen Namen, deine
                        E-Mail-Adresse und (optional) dein Profilbild. Wir speichern keine Google-Anmeldedaten
                        und können nicht auf dein Google-Konto zugreifen. Du kannst die Verknüpfung jederzeit
                        in deinem Google-Konto unter „Drittanbieter-Apps" widerrufen.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">8. Cookies &amp; lokaler Speicher</h2>
                    <p>Wir setzen folgende Cookies / Local-Storage-Einträge ein:</p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Technisch notwendig:</strong> Login-Token, Sprachpräferenz, Akkordeon-Zustände, Inserats-Entwurfsdaten. Rechtsgrundlage: Vertragserfüllung. Speicherdauer: Session bis 30 Tage.</li>
                        <li><strong>Funktional (optional):</strong> Theme-Präferenzen, Tour-Status. Speicherdauer: 12 Monate.</li>
                        <li><strong>Analyse (optional):</strong> nur nach Cookie-Banner-Einwilligung — anonymisiertes Klick-Tracking.</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">9. Speicherdauer</h2>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Kontodaten: bis zur Kündigung, danach max. 30 Tage in Backups</li>
                        <li>Inserate: 90 Tage nach Deaktivierung / Verkauf, danach Anonymisierung</li>
                        <li>Nachrichten: 12 Monate nach letzter Aktivität</li>
                        <li>Zahlungsbelege: 10 Jahre (§ 257 HGB)</li>
                        <li>KI-Logs: anonymisiert 30 Tage, danach Löschung</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">10. Deine Rechte</h2>
                    <p>
                        Du hast nach DSGVO Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17),
                        Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und Widerspruch
                        (Art. 21). Erteilte Einwilligungen kannst du jederzeit mit Wirkung für die Zukunft widerrufen
                        (Art. 7 Abs. 3). Wende dich an <a href="mailto:datenschutz@twowheels.info" className="underline">datenschutz@twowheels.info</a>.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">11. Automatisierte Entscheidungen / Profiling</h2>
                    <p>
                        Eine vollautomatisierte Entscheidungsfindung im Sinne des Art. 22 DSGVO findet nicht statt.
                        KI-Vorschläge sind ausdrücklich UNVERBINDLICH; sämtliche Veröffentlichungen, Käufe und
                        Vertragsabschlüsse erfordern eine bewusste Handlung des Nutzers.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">12. Sicherheit</h2>
                    <p>
                        Wir setzen Stand-der-Technik-Sicherheitsmaßnahmen ein: HTTPS / TLS-Verschlüsselung,
                        bcrypt-Passwort-Hashing, rollenbasierte Zugriffskontrollen, regelmäßige Sicherheitsupdates
                        und vom Internet abgeschottete Datenbanken. Die Plattform unterzieht sich regelmäßigen
                        Penetrationstests.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">13. Beschwerderecht</h2>
                    <p>
                        Du kannst dich bei jeder Aufsichtsbehörde — insbesondere der für unseren Sitz zuständigen
                        Landesdatenschutzbehörde — beschweren: Berliner Beauftragte für Datenschutz und
                        Informationsfreiheit, Alt-Moabit 59-61, 10555 Berlin.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-bold mb-2">14. Änderungen dieser Erklärung</h2>
                    <p>
                        Wir passen diese Datenschutzerklärung an, sobald sich rechtliche oder technische
                        Anforderungen ändern. Die aktuell gültige Fassung findest du immer hier mit Datum am
                        Anfang. Bei wesentlichen Änderungen informieren wir aktive Nutzer per E-Mail.
                    </p>
                </section>
            </article>
        </div>
    );
}
